import { useState, useEffect, useCallback } from "react";
import { Check, XCircle } from "lucide-react";
import MappingsTable from "../../components/adminDashboard/ui/usermodelgroupmapping/MappingsTable.jsx";
import AssignmentForm from "../../components/adminDashboard/ui/usermodelgroupmapping/AssignmentForm.jsx";
import LoadingState from "../../components/adminDashboard/ui/usermodelgroupmapping/LoadingState.jsx";
import {
  companyService,
  userService,
  modelConfigurationService,
  userModelGroupMappingService,
} from "../../services/index.js";

const readArray = (r) => {
  if (Array.isArray(r))            return r;
  if (Array.isArray(r?.data))      return r.data;
  if (Array.isArray(r?.data?.data)) return r.data.data;
  if (Array.isArray(r?.result))    return r.result;
  if (Array.isArray(r?.items))     return r.items;
  return [];
};

function Toast({ visible, message, type }) {
  if (!visible) return null;
  return (
    <div className="fixed top-6 right-6 z-[9999] flex items-center gap-3 px-5 py-3.5 rounded-2xl shadow-xl text-sm font-semibold animate-in slide-in-from-top-4 duration-300 bg-slate-900 dark:bg-white text-white dark:text-slate-900">
      <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${type === "success" ? "bg-[#36a852]" : "bg-red-500"}`}>
        {type === "success" ? <Check size={12} strokeWidth={3} /> : <XCircle size={12} />}
      </div>
      {message}
    </div>
  );
}

export default function UserModelGroupMapping() {
  const [showForm, setShowForm]             = useState(false);
  const [editingMapping, setEditingMapping] = useState(null);
  const [refreshKey, setRefreshKey]         = useState(0);
  const [toast, setToast]                   = useState({ visible: false, message: "", type: "success" });

  const [mappings,     setMappings]     = useState([]);
  const [companies,    setCompanies]    = useState([]);
  const [users,        setUsers]        = useState([]);
  const [modelGroups,  setModelGroups]  = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [searchQuery,  setSearchQuery]  = useState("");

  const showToast = (message, type = "success") => {
    setToast({ visible: true, message, type });
    setTimeout(() => setToast({ visible: false, message: "", type: "success" }), 3000);
  };

  // ── Fetch all data ──────────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      // 1. Companies + Users in parallel
      const [companiesRes, usersRes] = await Promise.all([
        companyService.getAllCompanies(),
        userService.getAllUsers("0"),
      ]);

      const companyList = readArray(companiesRes).map(c => ({
        id:   String(c.companyID ?? c.companyId ?? c.id ?? ""),
        name: c.companyName ?? c.name ?? "Unknown",
      }));
      setCompanies(companyList);

      const userList = readArray(usersRes).map(u => ({
        id:        String(u.userId ?? u.id ?? ""),
        name:      u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.name ?? u.userName ?? "Unknown",
        email:     u.email ?? u.officialEmail ?? "",
        companyId: String(u.companyId ?? u.companyID ?? ""),
      }));
      setUsers(userList);

      // 2. Model groups for all companies in parallel
      const groupResults = await Promise.allSettled(
        companyList.map(c => modelConfigurationService.getModelGroupByCompanyId(c.id).then(r => ({ r, companyId: c.id })))
      );
      const allGroups = [];
      groupResults.forEach(res => {
        if (res.status === "fulfilled") {
          const { r, companyId } = res.value;
          readArray(r).forEach(g => {
            const id = g.modelGroupId ?? g.id;
            if (!allGroups.find(x => String(x.id) === String(id))) {
              allGroups.push({ id, name: g.name ?? "Unknown Group", companyId });
            }
          });
        }
      });
      setModelGroups(allGroups);

      // 3. Mappings for all users in parallel
      const mappingResults = await Promise.allSettled(
        userList.map(user =>
          userModelGroupMappingService
            .getUserModelGroupMappingsByUser({ userId: user.id })
            .then(r => ({ r, user }))
        )
      );
      const allMappings = [];
      mappingResults.forEach(res => {
        if (res.status === "fulfilled") {
          const { r, user } = res.value;
          readArray(r).forEach(m => {
            const mgId = m.modelGroupId ?? m.modelGroupID;
            const group = allGroups.find(g => String(g.id) === String(mgId));
            allMappings.push({
              id:           m.id ?? m.mappingId,
              userId:       String(m.userId ?? m.userID ?? user.id),
              modelGroupId: mgId,
              isActive:     m.isActive ?? m.IsActive ?? true,
              createdAt:    m.createdAt ?? m.CreatedAt ?? "",
              createdBy:    m.createdBy ?? m.CreatedBy ?? "",
              companyId:    group?.companyId ?? user.companyId,
            });
          });
        }
      });
      setMappings(allMappings);
    } catch (e) {
      console.error("fetchAll error:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll, refreshKey]);

  // ── Lookup helpers ────────────────────────────────────────────────────────
  const getUserName       = (uid) => users.find(u => String(u.id) === String(uid))?.name ?? "Unknown User";
  const getUserEmail      = (uid) => users.find(u => String(u.id) === String(uid))?.email ?? "";
  const getModelGroupName = (gid) => modelGroups.find(g => String(g.id) === String(gid))?.name ?? `Group ${gid}`;
  const getCompanyName    = (cid) => companies.find(c => String(c.id) === String(cid))?.name ?? "Unknown Company";

  // ── Actions ───────────────────────────────────────────────────────────────
  const handleAddNew = () => { setEditingMapping(null); setShowForm(true); };

  const handleEdit = (mapping) => {
    const group = modelGroups.find(g => g.id === mapping.modelGroupId);
    setEditingMapping({ ...mapping, companyId: group?.companyId ?? mapping.companyId });
    setShowForm(true);
  };

  const handleDelete = async (mapping) => {
    try {
      await userModelGroupMappingService.deleteUserModelGroupMapping({
        companyId:    Number(mapping.companyId),
        modelGroupId: mapping.modelGroupId,
        userIds:      [Number(mapping.userId)],
        updatedBy:    Number(localStorage.getItem("userId") ?? 0),
      });
      showToast("Mapping deleted successfully", "success");
      setRefreshKey(k => k + 1);
    } catch (e) {
      console.error(e);
      showToast("Failed to delete mapping", "error");
    }
  };

  const handleToggleStatus = async (mapping) => {
    try {
      await userModelGroupMappingService.updateUserModelGroupMapping({
        companyId:    Number(mapping.companyId),
        modelGroupId: mapping.modelGroupId,
        userIds:      [Number(mapping.userId)],
        isActive:     !mapping.isActive,
        updatedBy:    Number(localStorage.getItem("userId") ?? 0),
      });
      showToast(`Assignment ${!mapping.isActive ? "activated" : "deactivated"}`, "success");
      setRefreshKey(k => k + 1);
    } catch (e) {
      console.error(e);
      showToast("Failed to update status", "error");
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingMapping(null);
    setRefreshKey(k => k + 1);
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingMapping(null);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  if (showForm) {
    return (
      <>
        <AssignmentForm
          editingMapping={editingMapping}
          onCancel={handleFormCancel}
          onSuccess={handleFormSuccess}
        />
        <Toast visible={toast.visible} message={toast.message} type={toast.type} />
      </>
    );
  }

  return (
    <>
      {loading ? (
        <LoadingState />
      ) : (
        <MappingsTable
          mappings={mappings}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onToggleStatus={handleToggleStatus}
          getUserName={getUserName}
          getUserEmail={getUserEmail}
          getModelGroupName={getModelGroupName}
          getCompanyName={getCompanyName}
          onAddNew={handleAddNew}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
        />
      )}
      <Toast visible={toast.visible} message={toast.message} type={toast.type} />
    </>
  );
}
